import type { Currency } from '@shared/utils';
import type { ExtraGameName, LotteryName, Participation, WinningInfo } from '../../../types';

/**
 * Registry and base functions for lottery-specific winnings calculators.
 *
 * This module implements a plugin pattern where each lottery can register its own
 * calculator function to handle special winnings calculations (e.g., multipliers).
 * If no calculator is registered for a lottery, the default calculator is used.
 *
 * @example
 * // Register a calculator (typically done in lottery-specific files)
 * registerCalculator('megaMillions', calculateMegaMillionsWinnings);
 *
 * // Use the calculator
 * const calculator = getCalculator('megaMillions');
 * const total = calculator?.({ participation, options, currency }) ?? calculateDefaultWinnings(...);
 */

type WinningsCalculationData = {
  participation: Participation;
  options?: string[];
  currency: Currency;
};

type WinningsCalculator = (data: WinningsCalculationData) => number;

const calculators: Partial<Record<LotteryName, WinningsCalculator>> = {};

/**
 * Registers a calculator function for a specific lottery.
 * Each lottery-specific file (e.g., megamillions.ts) should call this to register its calculator.
 */
function registerCalculator(lotteryName: LotteryName, calculator: WinningsCalculator) {
  calculators[lotteryName] = calculator;
}

/**
 * Retrieves the registered calculator for a lottery, or undefined if none exists.
 * Used by getLinesWinnings to determine which calculator to use.
 */
function getCalculator(lotteryName: LotteryName): WinningsCalculator | undefined {
  return calculators[lotteryName];
}

/**
 * Base function that extracts all winnings from lineWinningInfo.
 * This is the default behavior for most lotteries.
 */
function extractBaseWinnings(lineWinningInfo: Partial<Record<LotteryName | ExtraGameName, WinningInfo[]>>, currency: Currency): number[] {
  return Object.values(lineWinningInfo)
    .flat()
    .flatMap((winningInfo: WinningInfo) => winningInfo.winningPerLine?.[currency] ?? []);
}

/**
 * Default calculator that sums all winnings without any multipliers.
 */
function calculateDefaultWinnings(lineWinningInfo: Partial<Record<LotteryName | ExtraGameName, WinningInfo[]>>, currency: Currency): number {
  return extractBaseWinnings(lineWinningInfo, currency).reduce((sum: number, value: number) => sum + value, 0);
}

export { calculateDefaultWinnings, extractBaseWinnings, getCalculator, registerCalculator };
export type { WinningsCalculationData, WinningsCalculator };
